// One seat-fit definition for Places collections. Policy owns the Tight edge.
import { T } from "../app/tokens.js";
import { SPACE_POLICY_SEEDS, fitPolicy } from "./spacePolicyValues.js";
export const DEFAULT_BUFFER = SPACE_POLICY_SEEDS.hcBufferPct;
export const DENSE_SF_PER_SEAT = SPACE_POLICY_SEEDS.sfFloor;
export const SPACE_FIT_DEFINITION = "Space Fit = assigned today vs installed seats.";
export function eligibleSpaceFitOffice(office, asOf = new Date()) {
  if (!office) return false;
  const day = asOf.toISOString().slice(0, 10);
  const date = value => value instanceof Date ? value.toISOString().slice(0, 10) : String(value).slice(0, 10);
  if (office.onlineDate && date(office.onlineDate) > day) return false;
  return ["operational", "dormant"].includes(office.stage) && !office.deletedAt
    && office.disposition !== "exit"
    && !(office.exitDate && date(office.exitDate) <= day
      && (!office.reopenDate || date(office.reopenDate) > day));
}
export const FIT = Object.freeze({
  UNDER: "under", RIGHT: "comfortable", COMFORTABLE: "comfortable",
  TIGHT: "tight", OVER: "over", DENSIFY: "over",
  NO_DATA: "no_data", PARTIAL: "partial", NO_SEATS: "no_seats",
  EXCLUDED: "excluded", HISTORY: "history",
});
export const FIT_LABEL = Object.freeze({
  under: "Under-Used", comfortable: "Right-Sized", tight: "Tight", over: "Over-Cap",
  no_data: "No data", partial: "Partial", no_seats: "No seats",
  excluded: "No active office", history: "Under 3 months history",
});
export const FIT_STYLE = {
  under: { bg: T.border, ink: T.dark, border: T.borderDark },
  comfortable: { bg: "var(--v2-green-tint)", ink: "var(--v2-green)", border: "var(--v2-green-border)" },
  tight: { bg: T.ragAmberBg, ink: T.ragAmberText, border: T.ragAmberBorder },
  over: { bg: "var(--v2-red-tint)", ink: "var(--v2-red)", border: "var(--v2-red-border)" },
};
export const isFitBand = status => Object.hasOwn(FIT_STYLE, status);
export const tightBoundary = buffer => (100 - (Number.isFinite(buffer) ? buffer : DEFAULT_BUFFER)) / 100;
export function classifySeatFit(demand, installed, policy = SPACE_POLICY_SEEDS) {
  const p = fitPolicy(policy);
  if (!p) return FIT.NO_DATA;
  if (demand == null || !Number.isFinite(demand) || demand < 0) return FIT.PARTIAL;
  if (installed == null || !Number.isFinite(installed) || installed < 0) return FIT.NO_DATA;
  if (installed === 0) return demand > 0 ? FIT.OVER : FIT.NO_SEATS;
  const ratio = demand / installed;
  if (ratio < p.underUsedPct / 100) return FIT.UNDER;
  if (ratio < p.tightPct / 100) return FIT.RIGHT;
  return ratio <= 1 ? FIT.TIGHT : FIT.OVER;
}
export function seatFitBands(policy = SPACE_POLICY_SEEDS) {
  const p = fitPolicy(policy);
  if (!p) return [];
  const edge = p.tightPct;
  return [
    { status: FIT.UNDER, description: `less than ${p.underUsedPct}% of installed seats` },
    { status: FIT.RIGHT, description: `${p.underUsedPct}% to below ${edge}% of installed seats` },
    { status: FIT.TIGHT, description: `${edge}% through 100% of installed seats, inclusive` },
    { status: FIT.OVER, description: "above 100% of installed seats" },
  ].map(b => ({ ...b, label: FIT_LABEL[b.status], style: FIT_STYLE[b.status] }));
}
export function densifyingDisclosure(demand, usable, maximum) {
  return Number.isFinite(demand) && Number.isFinite(usable) && demand > usable
    && Number.isFinite(maximum) && demand <= maximum
    ? `Over usable seats; fits within max ${Math.round(maximum)} by densifying` : "";
}
export function officeSeatFit(office, policy = office.seatFitToday?.policy ?? SPACE_POLICY_SEEDS) {
  const source = office.seatFitToday;
  const status = !eligibleSpaceFitOffice(office) ? FIT.EXCLUDED
    : source?.state === "loading" ? "loading" : source?.state === "error" ? "error"
      : classifySeatFit(office.latestHeadcount?.totalCount ?? null, office.installedCapacity, policy);
  const p = fitPolicy(policy);
  const dense = status !== "excluded" && !!p && Number.isFinite(office.analyticalSqft) && office.analyticalSqft > 0 && office.installedCapacity > 0
    && office.analyticalSqft / office.installedCapacity < p.sfFloor;
  return { status, verdict: isFitBand(status) ? FIT_LABEL[status] : null, dense,
    disclosure: `${SPACE_FIT_DEFINITION} Assigned today ${office.latestHeadcount?.totalCount ?? "unknown"}; installed seats ${office.installedCapacity ?? "unknown"}. ${source?.error ?? ""}` };
}
